"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { motion } from "framer-motion";
import clsx from "clsx";

interface ScoreCardVideoProps {
  videoData: any;
  currentHoleNumber: number;
  currentShotNumber: number;
  setCurrentShotNumber: (currentShot: number) => void;
  setCurrentHoleNumber: (currentShot: number) => void;
  isScoreCardOpen: boolean;
  playerMuted?: boolean;
}

const ScoreCardVideo = ({
  videoData,
  currentHoleNumber,
  currentShotNumber,
  setCurrentShotNumber,
  setCurrentHoleNumber,
  isScoreCardOpen,
  playerMuted,
}: ScoreCardVideoProps) => {
  // @todo create custom controls for video player

  const [videoTotalTime, setVideoTotalTime] = useState<null | number>(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [playbackStarted, setPlaybackStarted] = useState(false);
  const [videoCompleted, setVideoCompleted] = useState(false);

  const playerRef = useRef<ReactPlayer | null>(null);

  const handleTimeUpdate = (e: any) => {
    const shots = videoData?.find(
      (hole: { holeNumber: number }) => hole.holeNumber === currentHoleNumber
    ).shots;
    setVideoCurrentTime(e.playedSeconds);
    if (
      e.playedSeconds === videoTotalTime &&
      currentShotNumber < shots[shots.length - 1].shotNumber
    ) {
      setVideoCurrentTime(0);
      const shotIndex = shots.findIndex(
        (shot: any) => shot.shotNumber === currentShotNumber
      );

      if (shotIndex < shots.length) {
        setCurrentShotNumber(shots[shotIndex + 1].shotNumber);
      }
    }
    if (
      e.playedSeconds === videoTotalTime &&
      currentShotNumber === shots[shots.length - 1].shotNumber &&
      currentHoleNumber < 18
    ) {
      setCurrentHoleNumber(currentHoleNumber + 1);
      setVideoCurrentTime(0);
      setCurrentShotNumber(1);
    }
  };

  const handleDurationChange = (duration: number) => {
    setVideoTotalTime(duration);
  };

  useEffect(() => {
    setVideoCurrentTime(0);
  }, [currentHoleNumber]);

  const findCurrentShot = videoData
    ?.find(
      (hole: { holeNumber: number }) => hole.holeNumber === currentHoleNumber
    )
    ?.shots?.find(
      (shot: { shotNumber: number }) => shot.shotNumber === currentShotNumber
    );

  return findCurrentShot?.videos && findCurrentShot?.videos[0]?.videoUrl ? (
    <div className="max-w-[1900px] pt-5 px-0 pb-12 md:px-5 md:pt-0">
      <ReactPlayer
        className={`flex rounded-[10px] overflow-hidden
           h-[180px] md:h-[280px] lg:h-[380px] min-[1311px]:!h-[580px]
        `}
        ref={playerRef}
        url={
          videoData
            .find(
              (hole: { holeNumber: number }) =>
                hole.holeNumber === currentHoleNumber
            )
            .shots.find(
              (shot: { shotNumber: number }) =>
                shot.shotNumber === currentShotNumber
            ).videos[0]?.videoUrl
        }
        playing={isScoreCardOpen}
        muted={playerMuted}
        playsinline
        controls
        width="100%"
        height="100%"
        progressInterval={250}
        onProgress={handleTimeUpdate}
        onDuration={handleDurationChange}
        config={{
          file: {
            attributes: {
              onContextMenu: (e: any) => e.preventDefault(),
              controlsList: "nodownload noplaybackrate",
            },
          },
        }}
      />

      <div className="flex justify-between items-center mt-10 mx-auto mb-0 max-w-[1200px]">
        {videoData
          .find(
            (hole: { holeNumber: number }) =>
              hole.holeNumber === currentHoleNumber
          )
          .shots.map((shot: { shotNumber: number }) => {
            return (
              <Fragment key={shot.shotNumber}>
                <div
                  className={clsx(
                    "cursor-pointer opacity-100 transition-opacity duration-300 bg-tm-c-brand min-w-5 h-5 flex items-center justify-center rounded-full text-[15px] hover:opacity-60 md:min-w-[26px] md:h-[26px]",
                    {
                      "opacity-60": shot.shotNumber === currentShotNumber,
                    }
                  )}
                  key={shot.shotNumber}
                  onClick={() => {
                    if (playbackStarted) {
                      setPlaybackStarted(false);
                    }
                    if (videoCompleted) {
                      setVideoCompleted(false);
                    }
                    setCurrentShotNumber(shot.shotNumber);
                    setVideoCurrentTime(0);
                    playerRef.current?.seekTo(0);
                  }}
                >
                  {shot.shotNumber}
                </div>
                <div className="w-full h-[5px] bg-[rgba(255,255,255,0.6)]">
                  {shot.shotNumber < currentShotNumber && (
                    <div className="h-[5px] bg-tm-c-brand max-w-full w-full" />
                  )}
                  {videoTotalTime && shot.shotNumber === currentShotNumber && (
                    <motion.div
                      className="h-[5px] bg-tm-c-brand max-w-full"
                      initial={{ width: "0%" }}
                      animate={{
                        width: `${(videoCurrentTime / videoTotalTime) * 100}%`,
                        transition: {
                          type: "tween",
                        },
                      }}
                      exit={{ width: "100%" }}
                    />
                  )}
                </div>
                {shot.shotNumber ===
                  videoData
                    .find(
                      (hole: { holeNumber: number }) =>
                        hole.holeNumber === currentHoleNumber
                    )
                    .shots.slice(-1)[0].shotNumber && (
                  <div className="cursor-pointer opacity-100 transition-opacity duration-300 bg-tm-c-brand min-w-5 h-5 flex items-center justify-center rounded-full text-[15px] hover:opacity-60 md:min-w-[26px] md:h-[26px]" />
                )}
              </Fragment>
            );
          })}
      </div>
    </div>
  ) : null;
};

export default ScoreCardVideo;
